import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import {
  alpha,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  List,
  ListItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { getIntlLocale } from '@/shared/i18n';
import { ErrorState, LoadingState } from '@/shared/ui';
import {
  ChannelBadge,
  ConversationStatusChip,
  EscalationReasonChip,
  IdleChip,
  formatDayTime,
  relativeTimeLabel,
} from '@/shared/ui/conversation';

import { useConversationEpisodes } from '../../hooks';
import { staffLabel } from '../../model/staff';
import type { ConversationDetail, ConversationEpisodeSummary } from '../../model/types';
import { EpisodeTopicEditor } from './EpisodeTopicEditor';

function CardShell({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.75 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

export interface EscalationPanelProps {
  detail: ConversationDetail;
  canViewChatbotSettings: boolean;
  canViewTemplates: boolean;
  /** The signed-in operator — decides the "you" variant of the staff-naming rule (design §13.2). */
  meId?: string | null;
}

/** A staff phrase rendered with its own tooltip when the directory could not be read (`UNAVAILABLE`). */
function StaffPhrase({ text, tooltip }: { text: string; tooltip?: string }) {
  return tooltip ? (
    <Tooltip title={tooltip}>
      <Typography component="span" variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  );
}

/**
 * Read-only escalation summary (design §7.3). The Acknowledge/Hand-back actions live only in
 * {@link ConversationHeader} — a deliberate simplification (disclosed in story 058's Notes) so the
 * version-checked mutation has a single call site rather than two independent instances racing on the
 * same episode `version`.
 */
export function EscalationPanel({
  detail,
  canViewChatbotSettings,
  canViewTemplates,
  meId,
}: EscalationPanelProps) {
  const { t } = useTranslation('conversations');
  const escalation = detail.openEscalation;
  if (!escalation) {
    return null;
  }
  const locale = getIntlLocale();
  const waitingSince = relativeTimeLabel(escalation.requestedAt, locale);
  const acker = staffLabel(escalation.ackedByRef, escalation.ackedBy, meId, t);
  const resolver = staffLabel(escalation.resolvedByRef, escalation.resolvedBy, meId, t);

  return (
    <CardShell title={t('detail.escalation.title')}>
      <Stack spacing={0.75}>
        <EscalationReasonChip reason={escalation.reason} />
        <Typography variant="body2">
          {t('detail.escalation.requested', {
            time: formatDayTime(escalation.requestedAt, locale),
          })}{' '}
          · {t('detail.escalation.waiting', { duration: waitingSince })}
        </Typography>
        {escalation.expiresAt ? (
          <Typography variant="body2" color="text.secondary">
            {t('detail.escalation.expires', { time: formatDayTime(escalation.expiresAt, locale) })}
          </Typography>
        ) : null}
        {escalation.status === 'ACKED' && escalation.ackedAt && acker ? (
          <StaffPhrase
            text={t('detail.escalation.acknowledgedBy', {
              time: formatDayTime(escalation.ackedAt, locale),
              name: acker.label,
            })}
            tooltip={acker.tooltip}
          />
        ) : null}
        {escalation.status === 'RESOLVED' && escalation.resolvedAt && resolver ? (
          <StaffPhrase
            text={t('detail.escalation.resolvedBy', {
              time: formatDayTime(escalation.resolvedAt, locale),
              name: resolver.label,
            })}
            tooltip={resolver.tooltip}
          />
        ) : null}
        {escalation.status === 'EXPIRED' ||
        (escalation.status === 'RESOLVED' && !(escalation.resolvedAt && resolver)) ? (
          <Chip size="small" label={t(`detail.escalation.status.${escalation.status}`)} />
        ) : null}
        {escalation.summary ? (
          <>
            <Typography variant="caption" color="text.secondary">
              {t('detail.escalation.summary')}
            </Typography>
            <Typography variant="body2">{escalation.summary}</Typography>
          </>
        ) : null}
        {typeof detail.unresolvedTurns === 'number' ? (
          <Typography variant="caption" color="text.secondary">
            {t('detail.escalation.unresolvedTurns', { count: detail.unresolvedTurns })}
          </Typography>
        ) : null}
        {canViewChatbotSettings ? (
          <MuiLink
            component={RouterLink}
            to={`/settings/chatbot?scope=escalation:${detail.lastChannel}`}
            variant="body2"
          >
            {t('detail.escalation.whyLink')}
          </MuiLink>
        ) : null}
        {escalation.reason === 'NO_TEMPLATE' ? (
          canViewTemplates ? (
            <MuiLink component={RouterLink} to="/settings/chatbot/templates" variant="body2">
              {t('detail.escalation.missingTexts')}
            </MuiLink>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('detail.escalation.missingTexts')}
            </Typography>
          )
        ) : null}
      </Stack>
    </CardShell>
  );
}

export function ContextSummaryCard({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation('conversations');
  if (!detail.contextSummary) {
    return null;
  }
  return (
    <CardShell title={t('detail.context.title')}>
      <Stack spacing={0.5}>
        <Typography variant="body2">{detail.contextSummary}</Typography>
        {detail.contextSummaryAt ? (
          <Typography variant="caption" color="text.secondary">
            {t('detail.context.asOf', {
              value: formatDayTime(detail.contextSummaryAt, getIntlLocale()),
            })}
          </Typography>
        ) : null}
      </Stack>
    </CardShell>
  );
}

export interface EpisodesPanelProps {
  conversationId: string;
  currentEpisodeId?: string | null;
  canManage: boolean;
  conflictActive: boolean;
  onVersionConflict: (sentVersion: number) => void;
  notify: (severity: 'success' | 'error' | 'info' | 'warning', message: string) => void;
  /** The signed-in operator — decides the "you" variant of the closed-by staff label (design §13.2). */
  meId?: string | null;
}

const EPISODES_PAGE_SIZE = 20;
const CLOSED_REASON_KEYS = [
  'IDLE_TIMEOUT',
  'OPERATOR_CLOSED',
  'CUSTOMER_DELETED',
  'MERGED',
  'THREAD_CLOSED',
];

/** The episode history (design §11.1) — newest first, current highlighted, inline topic rename. */
export function EpisodesPanel({
  conversationId,
  currentEpisodeId,
  canManage,
  conflictActive,
  onVersionConflict,
  notify,
  meId,
}: EpisodesPanelProps) {
  const { t } = useTranslation('conversations');
  const locale = getIntlLocale();
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<ConversationEpisodeSummary[]>([]);
  const query = useConversationEpisodes(conversationId, { page, size: EPISODES_PAGE_SIZE });

  useEffect(() => {
    setPage(0);
    setItems([]);
  }, [conversationId]);

  useEffect(() => {
    // `useConversationEpisodes` keeps the PREVIOUS page's data (`placeholderData: keepPreviousData`)
    // visible while the new page loads, so `query.data` changes reference to the OLD page the instant
    // `page` itself changes. Appending on that render duplicated the previous page's rows once before
    // the real page arrived, and then again for real when it did (WP5 review). Skip the placeholder
    // render entirely — the effect fires again, for real, once the fetch actually settles.
    if (!query.data || query.isPlaceholderData) return;
    setItems((prev) => (page === 0 ? query.data.items : [...prev, ...query.data.items]));
  }, [query.data, page, query.isPlaceholderData]);

  const total = query.data?.total ?? items.length;

  return (
    <CardShell title={t('episodes.title')} action={<Chip size="small" label={total} />}>
      {query.isLoading && items.length === 0 ? (
        <LoadingState fullHeight={false} label={t('common:status.loading')} />
      ) : query.isError && items.length === 0 ? (
        <ErrorState
          title={t('episodes.loadError')}
          message={query.error?.message}
          onRetry={() => void query.refetch()}
        />
      ) : total === 1 ? (
        <Typography variant="body2" color="text.secondary">
          {t('episodes.first')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {items.map((item) => {
            const isCurrent = item.episodeId === currentEpisodeId;
            return (
              <ListItem
                key={item.episodeId}
                alignItems="flex-start"
                disableGutters
                sx={{
                  display: 'block',
                  bgcolor: isCurrent
                    ? (theme: Theme) => alpha(theme.palette.primary.main, 0.06)
                    : undefined,
                  borderLeft: isCurrent ? '3px solid' : '3px solid transparent',
                  borderLeftColor: isCurrent ? 'primary.main' : 'transparent',
                  pl: 1,
                  py: 0.5,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  {/* Found while reading, fixed in the same change (design §13.2): this used to render
                      the raw enum (e.g. "WAITING_OPERATOR") instead of the localised status chip. */}
                  <ConversationStatusChip status={item.status} />
                  {item.idle ? <IdleChip lastActivityAt={item.lastActivityAt} /> : null}
                  {isCurrent ? (
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
                      {t('episodes.current')}
                    </Typography>
                  ) : null}
                </Stack>
                <EpisodeTopicEditor
                  conversationId={conversationId}
                  episodeId={item.episodeId}
                  topic={item.topic}
                  version={item.version}
                  canManage={canManage}
                  disabled={conflictActive}
                  onVersionConflict={onVersionConflict}
                  onSaved={() => notify('success', t('episodes.topicSaved'))}
                  notify={notify}
                />
                <Typography variant="caption" color="text.secondary" component="div">
                  {t('episodes.started', {
                    time: formatDayTime(item.startedAt, locale),
                    channel: t(`channel.${item.openedByChannel}`, item.openedByChannel),
                  })}
                </Typography>
                {item.closedAt
                  ? (() => {
                      // OPERATOR_CLOSED with a resolvable staff actor names them (design §13.2); every
                      // other reason (or an operator-closed row with no ref/id at all — an older BFF)
                      // keeps the unchanged reason sentence.
                      const closer = staffLabel(item.closedByRef, item.closedBy, meId, t);
                      if (item.closedReason === 'OPERATOR_CLOSED' && closer) {
                        return (
                          <Tooltip
                            title={closer.tooltip ?? ''}
                            disableHoverListener={!closer.tooltip}
                          >
                            <Typography variant="caption" color="text.secondary" component="div">
                              {t('episodes.closedBy', {
                                time: formatDayTime(item.closedAt, locale),
                                name: closer.label,
                              })}
                            </Typography>
                          </Tooltip>
                        );
                      }
                      return (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {t('episodes.closed', {
                            time: formatDayTime(item.closedAt, locale),
                            reason:
                              item.closedReason && CLOSED_REASON_KEYS.includes(item.closedReason)
                                ? t(`episodes.closedReasons.${item.closedReason}`)
                                : (item.closedReason ?? ''),
                          })}
                        </Typography>
                      );
                    })()
                  : null}
                {item.openIssueRef ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {t('episodes.issueRef', { ref: item.openIssueRef })}
                  </Typography>
                ) : null}
              </ListItem>
            );
          })}
        </List>
      )}
      {items.length < total ? (
        <Button
          size="small"
          variant="text"
          fullWidth
          disabled={query.isFetching}
          onClick={() => setPage((p) => p + 1)}
          startIcon={query.isFetching ? <CircularProgress size={14} /> : undefined}
        >
          {t('episodes.showMore')}
        </Button>
      ) : null}
    </CardShell>
  );
}

export function CustomerCard({
  detail,
  canViewCustomer,
}: {
  detail: ConversationDetail;
  canViewCustomer: boolean;
}) {
  const { t } = useTranslation('conversations');
  const nameHidden = !detail.provisional && detail.displayName == null;
  return (
    <CardShell
      title={t('detail.customer.title')}
      action={<PersonRoundedIcon fontSize="small" color="action" />}
    >
      <Stack spacing={0.5}>
        {detail.provisional ? (
          <Typography variant="body2" color="text.secondary">
            {t('detail.customer.provisional')}
          </Typography>
        ) : (
          <>
            <Typography variant="body2">
              {nameHidden ? t('detail.header.customerFallback') : detail.displayName}
            </Typography>
            {detail.language ? (
              <Typography variant="caption" color="text.secondary">
                {t('detail.customer.language', { value: detail.language })}
              </Typography>
            ) : null}
            {detail.customerId && canViewCustomer ? (
              <MuiLink
                component={RouterLink}
                to={`/customers/${detail.customerId}`}
                variant="body2"
              >
                {t('detail.openInCustomers')}
              </MuiLink>
            ) : null}
          </>
        )}
      </Stack>
    </CardShell>
  );
}

export function SessionsCard({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation('conversations');
  const locale = getIntlLocale();
  return (
    <CardShell
      title={t('detail.sessions.title')}
      action={<RouterRoundedIcon fontSize="small" color="action" />}
    >
      <Stack spacing={1}>
        {detail.sessions.map((session) => (
          <Stack
            key={session.id}
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <ChannelBadge channel={session.channel} size="small" />
            <Chip
              size="small"
              variant="outlined"
              color={session.reachable ? 'success' : 'error'}
              label={t(session.reachable ? 'detail.sessions.reachable' : 'detail.sessions.blocked')}
            />
            {session.locale ? (
              <Typography variant="caption" color="text.secondary">
                {session.locale}
              </Typography>
            ) : null}
            {session.reachable && session.lastActivityAt ? (
              <Typography variant="caption" color="text.secondary">
                {t('detail.sessions.lastActivity', {
                  value: relativeTimeLabel(session.lastActivityAt, locale),
                })}
              </Typography>
            ) : null}
          </Stack>
        ))}
      </Stack>
    </CardShell>
  );
}
